// # Anki Guide Component
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOutlined, 
  BulbOutlined, 
  SettingOutlined, 
  LineChartOutlined,
  RocketOutlined
} from '@ant-design/icons';
import DashboardHeader from '../../../components/DashboardHeader';
import Header from '../../../landing/components/header';  // # Import regular header
import DashboardFooter from '../../../components/DashboardFooter';
import AnkiIcon from '../../../aneo_media/community/guide/anki.png';
import './guide_anki.css';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';
import { useAuthStore } from '../../../auth/state';  // # Import auth store

const AnkiGuide = () => {
  const navigate = useNavigate();
  const sectionRefs = useRef([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { isAuthenticated } = useAuthStore();  // # Get auth state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Helper function to handle scroll animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // # Add animation class to the section itself
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          
          // # Add animation to QA items
          const qaItems = entry.target.querySelectorAll('.gde_anki_mn_qa-item');
          qaItems.forEach((item, i) => {
            const baseDelay = 0.1;
            const staggerDelay = i * 0.15;
            item.style.setProperty('--delay', `${baseDelay + staggerDelay}s`);
            item.style.opacity = '1';
            item.style.transform = 'translateX(0)';
            item.classList.add('gde_anki_mn_animate');
          });
          
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    
    // # Ensure sections are being observed
    const sections = document.querySelectorAll('.gde_anki_mn_section');
    sections.forEach(section => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => observer.disconnect();
  }, []);

  // # Add console log to verify sections data
  useEffect(() => {
    console.log('Sections data:', sections);
  }, []);

  // # Helper function to create safe links
  const SafeLink = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="gde_anki_mn_link">
      {children}
    </a>
  );

  // # Define sections with Q&A content and Ant Design icons
  const sections = [
    {
      title: 'General Questions',
      icon: <BookOutlined className="gde_anki_mn_section-icon" />,
      questions: [
        {
          q: 'What is Anki, and why should I use it for USMLE preparation?',
          a: <>Anki is a spaced repetition flashcard app that helps reinforce memory retention. Learn more about it at <SafeLink href="https://apps.ankiweb.net/">Anki's official website</SafeLink>. It is widely used for USMLE prep due to its efficiency in long-term recall.</>
        },
        {
          q: 'How does Anki\'s spaced repetition system (SRS) work?',
          a: 'Anki schedules reviews based on how well you remember a card. The better you recall it, the longer the interval before you see it again.'
        },
        {
          q: 'What are the best Anki decks for USMLE Step 1 and Step 2?',
          a: <>Popular decks include those from <SafeLink href="https://www.theanking.com/">AnKing</SafeLink>, Zanki, Brosencephalon, and Lightyear for Step 1, while Anking Step 2, Dorian, and Clerkship Decks are great for Step 2.</>
        },
        {
          q: 'Is Anki free to use?',
          a: 'Yes, Anki is free on Windows, Mac, and Android. The iOS version requires a one-time purchase.'
        }
      ]
    },
    {
      title: 'Getting Started with Anki',
      icon: <BulbOutlined className="gde_anki_mn_section-icon" />,
      questions: [
        {
          q: 'How do I download and install Anki?',
          a: <>Visit <SafeLink href="https://apps.ankiweb.net/">apps.ankiweb.net</SafeLink> to download Anki for your platform.</>
        },
        {
          q: 'How do I import a pre-made USMLE deck?',
          a: 'Download the deck (.apkg file), open Anki, and import the file by clicking File > Import.'
        },
        {
          q: 'What are tags, and how can I use them to organize my decks?',
          a: 'Tags allow you to categorize flashcards by subject or system. You can filter reviews based on tags for targeted studying.'
        },
        {
          q: 'How do I sync my Anki cards across multiple devices?',
          a: <>Create an <SafeLink href="https://ankiweb.net/about">AnkiWeb</SafeLink> account and enable syncing from Preferences Network.</>
        }
      ]
    },
    {
      title: 'Using Anki Effectively',
      icon: <SettingOutlined className="gde_anki_mn_section-icon" />,
      questions: [
        {
          q: 'What is the best way to review Anki cards?',
          a: 'Use active recall and spaced repetition. Avoid mindless reviewing and instead focus on understanding concepts.'
        },
        {
          q: 'How many Anki cards should I review per day?',
          a: 'For Step 1, aim for 800-1000 reviews/day in dedicated study periods. During coursework, 100-200 reviews/day is reasonable.'
        },
        {
          q: 'Should I make my own cards or use pre-made decks?',
          a: <>Pre-made decks save time, but making custom cards for weak areas improves retention. We can guide on which will suit your path best.</>
        },
        {
          q: 'What is Anki\'s "bury" function, and when should I use it?',
          a: 'Burying temporarily hides related cards to avoid repetitive exposure on the same day.'
        },
        {
          q: 'What settings should I use for optimal spaced repetition?',
          a: <>Use Anking's recommended settings, which adjust learning steps, intervals, and leech handling. Watch their detailed guide on <SafeLink href="https://www.youtube.com/theanking">AnKing's YouTube channel</SafeLink>.</>
        }
      ]
    },
    {
      title: 'Advanced Features & Troubleshooting',
      icon: <LineChartOutlined className="gde_anki_mn_section-icon" />,
      questions: [
        {
          q: 'What are filtered decks, and how do I use them?',
          a: 'Filtered decks allow you to study specific cards, such as missed or difficult ones, separately.'
        },
        {
          q: 'How do I suspend or delete cards I don\'t need?',
          a: 'Right-click on a card and select Suspend to remove it from daily reviews without deleting it.'
        },
        {
          q: 'Can I reset a deck if I want to start over?',
          a: 'Yes, you can Reschedule or Forget cards via the Browse menu.'
        },
        {
          q: 'My Anki is lagging or slow. How can I fix it?',
          a: 'Try reducing add-ons, optimizing database performance (Tools > Check Database), and limiting new cards per day.'
        },
        {
          q: 'How can I track my progress using Anki?',
          a: 'Use the Stats feature to monitor daily reviews, retention rates, and learning efficiency.'
        },
        {
          q: 'Where can I find more resources to improve my Anki workflow?',
          a: <>Check <SafeLink href="https://www.reddit.com/r/medicalschoolanki/">r/medicalschoolanki</SafeLink>, <SafeLink href="https://www.youtube.com/theanking">AnKing's YouTube channel</SafeLink>, and <SafeLink href="https://www.youtube.com/watch?v=AbvaITy3oeQ">Med School Insiders' Anki guide</SafeLink> for tips and updates. Our mentorship program offers extensive guidance on excelling with Anki.</>
        }
      ]
    }
  ];

  return (
    <div className={`gde_anki_mn_page-container ${isDarkMode ? 'gde_anki_mn_dark-mode' : ''}`}>
      {isAuthenticated ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}

      <div className="gde_anki_mn_content-container">
        <div className="gde_anki_mn_header">
          <div className="gde_anki_mn_header-content">
            <img src={AnkiIcon} alt="Anki Icon" className="gde_anki_mn_header-icon" />
            <div className="gde_anki_mn_header-text">
              <h1>Anki Q&A for USMLE Preparation</h1>
              <p>Your comprehensive guide to mastering Anki for USMLE success</p>
            </div>
          </div>
        </div>

        <div className="gde_anki_mn_sections">
          {sections.map((section, index) => (
            <div 
              key={index} 
              className="gde_anki_mn_section"
              ref={el => sectionRefs.current[index] = el}
            >
              <div className="gde_anki_mn_section-header">
                {section.icon}
                <h2>{section.title}</h2>
              </div>
              
              <div className="gde_anki_mn_qa-list">
                {section.questions.map((qa, qaIndex) => (
                  <div key={qaIndex} className="gde_anki_mn_qa-item">
                    <div className="gde_anki_mn_question">
                      <strong>Q{qaIndex + 1}:</strong> {qa.q}
                    </div>
                    <div className="gde_anki_mn_answer">
                      {qa.a}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* # Add CTA Section */}
          <div className="gde_anki_mn_cta-section">
            <div className="gde_anki_mn_cta-content">
              <div className="gde_anki_mn_cta-icon">
                <RocketOutlined />
              </div>
              <h2>Ready to Excel in Your USMLE Journey?</h2>
              <p>We offer personalized and perfectly-designed mentorship programs for USMLE Step 1 & 2</p>
              <button 
                className="gde_anki_mn_cta-button"
                onClick={() => navigate('/products')}
              >
                Explore Our Programs
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* # Pass theme prop to footer */}
      <div className="gde_anki_mn_footer-wrapper">
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
    </div>
  );
};

export default AnkiGuide;
